import { Controller } from "@hotwired/stimulus"
import { savePlan, debounce, registerFlush } from "plan_storage"
import { scheduleRowsHtml } from "schedule_fields"
import { renderIcons } from "lucide_icons"
import { libFamily, serviceLibList, serviceLibAdd, serviceLibCategories } from "goal_library"
import { vendorGet, resolveVendorDisplay } from "vendor_library"

// Goal-resources editor — manages the nested list of resources on a
// single goal. Mirrors the legacy editor at pcp_builder.html#9709
// goalCardHtml's resource list, but in the Rails-native editor shape.
//
// One controller instance per goal row. The owning goal is identified
// by its id via `data-goal-resources-goal-id-value`.
//
// Per resource, the editor exposes:
//   - tag (segmented: Generic | RC Funded | Unmet Need)
//   - text (service type / name)
//   - comment (free-text qualifier)
//   - currentServices (Currently Approved / Requesting description)
//   - ratio (e.g. "2:1")
//   - schedule[] — one row per schedule entry with dayType, times,
//     hours, mins, cost. Each schedule row reads as a frequency chip
//     in the preview.
//
// State lives at plan.goals[goalIdx].resources / .schedule. Every
// mutation persists to localStorage under the same key the iframe
// editor uses, then dispatches plan:change so the preview / wizard
// can hot-reload without a navigation.
const STORAGE_KEY = "pcp_plan_data"

const TAGS = [
  { key: "general", label: "Generic",    cls: "bg-blue-100 text-blue-700"   },
  { key: "rc",      label: "RC Funded",  cls: "bg-green-100 text-green-700" },
  { key: "unmet",   label: "Unmet Need", cls: "bg-amber-100 text-amber-700" }
]

// DAY_TYPES now lives in schedule_fields.js (shared with
// current_services_list_controller). Imported at the top.

// Map each dayType to the number of times that period repeats per
// year. Used to project a schedule row into annual trip counts for
// the Total Annual Travel calculation. "As Needed" and unmapped types
// fall through to 0 → contribute nothing to the annual sum.
const ANNUAL_PERIODS = {
  "Daily": 365,
  "Mon-Fri": 260, "Sat-Sun": 104, "Non-School": 0,
  "Weekly": 52, "Bi-Weekly": 26, "Monthly": 12, "Quarterly": 4,
  "Semi-Annually": 2, "Annually": 1, "As Needed": 0,
  "Mon-Wed-Fri": 156, "Tue-Thu": 104, "Mon-Wed": 104, "Tue-Thu-Sat": 156,
  "Mon": 52, "Tue": 52, "Wed": 52, "Thu": 52, "Fri": 52, "Sat": 52, "Sun": 52
}

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
}[c]))

// Standard service catalog for the Service <select>. Starts from
// IRC_SERVICES in the legacy editor (pcp_builder.html). Entries are
// plain options except "Social Rec", which is a CATEGORY (rendered as
// an <optgroup>) grouping the recreation/leisure services the org funds
// under DDS Social Recreation. Legacy itself is left untouched.
const SERVICE_GROUPS = [
  "Adaptive Skills Training",
  "Adult Day Activities Program",
  "Assessment and Consultation",
  "Assistive Technology: Augmentative and Alternative Communication (AAC)",
  "Behavior Services",
  "Childcare Services",
  "Community Integration Training",
  "Coordinated Family Support Services (CFS)",
  "Crisis Intervention Services",
  "Durable Medical Equipment",
  "Employment Programs",
  "Family Home Agency (FHA) / Family Foster Agency (FFA)",
  "Family Support",
  "Health Care Facilities (HCF)",
  "Home Health Supports",
  "Independent Living Services (ILS)",
  "Infant Development Services",
  "Insurance, co-payments, coinsurance, and deductibles",
  "Medical Specialists and Professionals",
  "Mobility Training",
  "Personal Assistance / Personal Care Services",
  "Pharmaceutical Services",
  "Residential Care Homes",
  "Respite Services (In-Home)",
  "Self-Determination",
  "Service Coordination",
  { label: "Social Rec", items: ["Music Lessons", "Dance Lessons", "Art Lessons", "Day Camp", "Overnight Camp"] },
  { label: "Health and Safety", items: ["Swimming Lessons"] },
  "Non Medical",
  "Education",
  "Social Skills Training / Classes",
  "Supplemental Program Supports",
  "Supported Living Services",
  "Therapy Services",
  "Translation / Interpreter Services",
  "Transportation",
  "Other"
]

// Flat list of every selectable value — used to decide whether a stored
// value is a known service (else it gets pinned as its own option).
const IRC_SERVICES = SERVICE_GROUPS.flatMap(s => (typeof s === "string" ? [s] : s.items))

// Build the <option> markup for the Service <select>. The standard
// choices are IRC_SERVICES (identical to the legacy editor's list). A
// stored value that isn't one of them — a service name typed in the
// older free-text field, or imported/legacy data — is kept as a clean
// selected option up top so it reads like any other choice and nothing
// silently changes. An unset resource shows a blank prompt instead of
// defaulting to the first service.
const serviceOptions = (current) => {
  const cur = String(current == null ? "" : current).trim()
  // Saved custom services carry their sub-section (category). They count as
  // "known" so a selected one isn't also pinned as a stray option, and they're
  // merged into the matching sub-section below.
  const customServices = serviceLibList() // [{ name, category }]
  const known = new Set([...IRC_SERVICES, ...customServices.map(c => c.name)])
  const byCat = {}
  customServices.forEach(c => { (byCat[c.category] ||= []).push(c.name) })
  const parts = []
  if (!cur) {
    parts.push(`<option value="" selected>Select a service…</option>`)
  } else if (!known.has(cur)) {
    parts.push(`<option value="${esc(cur)}" selected>${esc(cur)}</option>`)
  }
  const opt = (s) => `<option value="${esc(s)}"${cur === s ? " selected" : ""}>${esc(s)}</option>`
  const builtinLabels = new Set()
  SERVICE_GROUPS.forEach(entry => {
    if (entry === "Other") return // rendered inside the "Other" group below
    if (typeof entry === "string") {
      parts.push(opt(entry))
    } else {
      builtinLabels.add(entry.label)
      // Built-in sub-section, plus any custom services the user filed under it.
      const items = [...entry.items, ...(byCat[entry.label] || [])]
      parts.push(`<optgroup label="${esc(entry.label)}">${items.map(opt).join("")}</optgroup>`)
    }
  })
  // "General" custom services sit at the top level (no sub-section).
  ;[...(byCat["General"] || []), ...(byCat[""] || [])].forEach(s => parts.push(opt(s)))
  // Custom sub-sections the user created that aren't built in → their own group.
  Object.keys(byCat).forEach(cat => {
    if (cat === "Other" || cat === "General" || cat === "" || builtinLabels.has(cat)) return
    parts.push(`<optgroup label="${esc(cat)}">${byCat[cat].map(opt).join("")}</optgroup>`)
  })
  // "Other" sub-section: its custom services + the "specify…" trigger that
  // reveals the free-text + category + Add-to-library controls.
  const otherOpts = (byCat["Other"] || []).map(opt).join("") +
    `<option value="Other"${cur === "Other" ? " selected" : ""}>➕ Other / add new…</option>`
  parts.push(`<optgroup label="Other">${otherOpts}</optgroup>`)
  return parts.join("")
}

export default class extends Controller {
  static values = { goalId: String }
  static targets = ["list"]

  connect() {
    this.plan = this._load()
    // Watch the closest [data-row] ancestor so we pick up dynamic-list's
    // row id once it lands. dynamic-list assigns row.dataset.id AFTER
    // cloning the template, so a one-shot read on connect would miss it.
    const row = this.element.closest("[data-row]")
    if (row) {
      this._rowObserver = new MutationObserver(() => {
        if (row.dataset.id && row.dataset.id !== this._knownGoalId) {
          this._knownGoalId = row.dataset.id
          this.render()
        }
      })
      this._rowObserver.observe(row, { attributes: true, attributeFilter: ["data-id"] })
      // Initial read in case data-id is already set (e.g. re-render).
      if (row.dataset.id) this._knownGoalId = row.dataset.id
    }
    this.render()
    this._onChange = (e) => {
      if (this._suppress) return
      this.plan = (e.detail && e.detail.plan) || this._load()
      this.render()
    }
    document.addEventListener("plan:change", this._onChange)
    // Register a global flusher so a pending resource edit is written on tab
    // close / hide AND when another section (e.g. adding a goal) flushes before
    // a wholesale array write — the fix for "edit a resource, add a goal, lose it".
    this._unregFlush = registerFlush(() => this._debPersist?.flush())
  }

  disconnect() {
    this._debPersist?.flush()
    this._unregFlush?.()
    document.removeEventListener("plan:change", this._onChange)
    if (this._rowObserver) this._rowObserver.disconnect()
  }

  // ---- mutations ----
  addResource() {
    const g = this._goal()
    if (!g) return
    if (!Array.isArray(g.resources)) g.resources = []
    g.resources.push({
      id: Date.now() + Math.random(),
      tag: "general",
      text: "",
      comment: "",
      servicesLocation: "",
      vendorName: "",
      vendorPhone: "",
      vendorEmail: "",
      vendorWebsite: "",
      vendorDetails: "",   // legacy mashup, kept for backward read compat
      vendorAddress: "",
      mileage: "",
      cost: "",
      ratio: "",
      vendors: [],
      schedule: []
    })
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  removeResource(event) {
    const rid = event.currentTarget.dataset.resourceId
    const g = this._goal()
    if (!g || !Array.isArray(g.resources)) return
    g.resources = g.resources.filter(r => String(r.id) !== String(rid))
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  // The Service <select> — writes both r.text (native's canonical service
  // field) and r.type (legacy's, kept in sync), then re-renders so the
  // "Specify the service" input (shown only when "Other" is selected)
  // toggles into or out of view immediately.
  changeService(event) {
    const el = event.currentTarget
    const rid = el.dataset.resourceId
    const r = this._resource(rid)
    if (!r) return
    r.text = el.value
    r.type = el.value
    this._persist({ nested: true })
    // Toggle the "Specify the service" input in place — NO re-render.
    // Re-rendering rebuilds the list (and the <select> the user just
    // used) and, because the field changes the list height, moves the
    // scroll position. Show the input only when "Other" is selected.
    const spec = this.listTarget?.querySelector(
      `input[data-field="comment"][data-resource-id="${CSS.escape(String(rid))}"]`
    )
    if (spec) {
      const isOther = el.value === "Other"
      const row = spec.closest("[data-svc-other-row]") || spec
      row.hidden = !isOther
      if (isOther) spec.focus({ preventScroll: true })
    }
  }

  // Save the typed "Other" service to the user's library, then adopt it as this
  // resource's service — so it reads like a normal choice here and shows up in
  // the "Other" group of every resource picker from now on.
  addServiceToLibrary(event) {
    const rid = event.currentTarget.dataset.resourceId
    const r = this._resource(rid)
    if (!r) return
    const name = String(r.comment || "").trim()
    if (!name) {
      const inp = this.listTarget?.querySelector(`input[data-field="comment"][data-resource-id="${CSS.escape(String(rid))}"]`)
      if (inp) inp.focus({ preventScroll: true })
      return
    }
    const catEl = this.listTarget?.querySelector(`input[data-svc-cat][data-resource-id="${CSS.escape(String(rid))}"]`)
    const category = String(catEl?.value || "Other").trim() || "Other"
    serviceLibAdd(name, category)
    r.text = name
    r.type = name
    r.comment = ""
    r.currentServices = this._composeCurrentServices(r)
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  // ---- vendors (multiple providers per resource) ----
  addVendor(event) {
    const r = this._resource(event.currentTarget.dataset.resourceId)
    if (!r) return
    if (!Array.isArray(r.vendors)) r.vendors = []
    r.vendors.push({
      id: Date.now() + Math.random(),
      vendorId: "",  // linked vendor library id (empty = unlinked/legacy)
      name: "", phone: "", email: "", website: "", address: "",
      servicesLocation: "", cost: ""
    })
    r.currentServices = this._composeCurrentServices(r)
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  removeVendor(event) {
    const el = event.currentTarget
    const r = this._resource(el.dataset.resourceId)
    if (!r || !Array.isArray(r.vendors)) return
    r.vendors = r.vendors.filter(v => String(v.id) !== String(el.dataset.vendorId))
    r.currentServices = this._composeCurrentServices(r)
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  // Leaf update for one vendor field. nested:true so the outer list
  // doesn't re-render (keeps caret + scroll). Keeps the composed
  // currentServices (read by the preview/PDF) in sync, and refreshes the
  // aggregate Total Cost in place when a vendor's cost changes.
  updateVendorField(event) {
    const el = event.currentTarget
    const r = this._resource(el.dataset.resourceId)
    const field = el.dataset.field
    if (!r || !Array.isArray(r.vendors) || !field) return
    const v = r.vendors.find(x => String(x.id) === String(el.dataset.vendorId))
    if (!v) return
    v[field] = el.value
    if (["name", "phone", "email", "address", "servicesLocation"].includes(field)) {
      r.currentServices = this._composeCurrentServices(r)
    }
    // Location gates this vendor's Mileage + Travel rows (no travel from
    // home / telehealth). Toggle them in place — no re-render, scroll holds.
    if (field === "servicesLocation") this._toggleVendorTravelRows(v)
    this._persistDebounced()
    // Refresh THIS vendor's own Total Cost / Travel in place — each
    // vendor is priced independently; there is no combined total.
    if (field === "cost" || field === "servicesLocation") this._refreshVendorCost(r, v)
    if (field === "mileage" || field === "servicesLocation") this._refreshVendorTravel(r, v)
  }

  // Fill this vendor row from a saved (library) vendor chosen in the
  // "Choose from saved vendors" dropdown. Overwrites name + contact fields;
  // the select resets to its placeholder on re-render.
  fillVendorFromLibrary(event) {
    const el = event.currentTarget
    const r = this._resource(el.dataset.resourceId)
    if (!r || !Array.isArray(r.vendors)) return
    const v = r.vendors.find(x => String(x.id) === String(el.dataset.vendorId))
    if (!v) return
    const opt = el.selectedOptions && el.selectedOptions[0]
    if (!opt || !opt.value) return
    v.name    = opt.value
    v.phone   = opt.dataset.phone   || ""
    v.email   = opt.dataset.email   || ""
    v.website = opt.dataset.website || ""
    if (opt.dataset.address) v.address = opt.dataset.address
    r.currentServices = this._composeCurrentServices(r)
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  // Handle vendor-picker:change events. Links this vendor entry to the library.
  handleVendorPickerChange(event) {
    const el = event.currentTarget
    const r = this._resource(el.dataset.resourceId)
    if (!r || !Array.isArray(r.vendors)) return
    const v = r.vendors.find(x => String(x.id) === String(el.dataset.vendorId))
    if (!v) return
    const { id: vendorId, vendor } = event.detail || {}
    v.vendorId = vendorId || ""
    // Copy library fields to legacy fields for backward compatibility
    if (vendor) {
      v.name    = vendor.name || ""
      v.phone   = vendor.phone || ""
      v.email   = vendor.email || ""
      v.website = vendor.website || ""
      const locs = Array.isArray(vendor.locations) ? vendor.locations : []
      v.address = locs.length > 0 && locs[0].address ? locs[0].address : ""
    }
    r.currentServices = this._composeCurrentServices(r)
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  // Unlink a vendor from the library (reverts to editable legacy mode)
  unlinkVendor(event) {
    event.preventDefault()
    const el = event.currentTarget
    const r = this._resource(el.dataset.resourceId)
    if (!r || !Array.isArray(r.vendors)) return
    const v = r.vendors.find(x => String(x.id) === String(el.dataset.vendorId))
    if (!v) return
    v.vendorId = ""
    r.currentServices = this._composeCurrentServices(r)
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  _isTravelLoc(loc) {
    return !!loc && loc !== "In Home" && loc !== "Telehealth"
  }

  _toggleVendorTravelRows(v) {
    const key = CSS.escape(String(v.id))
    const show = this._isTravelLoc(v.servicesLocation)
    const mrow = this.listTarget?.querySelector(`[data-vendor-mileage-row][data-vendor-id="${key}"]`)
    const trow = this.listTarget?.querySelector(`[data-vendor-travel-row][data-vendor-id="${key}"]`)
    if (mrow) mrow.hidden = !show
    if (trow) trow.hidden = !show
  }

  _refreshVendorCost(r, v) {
    const wrap = this.listTarget?.querySelector(`[data-cost-total-vid='${CSS.escape(String(v.id))}']`)
    if (!wrap) return
    const c = this._computeAnnualCost({ cost: v.cost, schedule: r.schedule })
    wrap.innerHTML = c
      ? this._formatCostRollup(c)
      : `<span class="text-gray-400 italic">Enter Cost + Frequency to compute</span>`
  }

  _refreshVendorTravel(r, v) {
    const wrap = this.listTarget?.querySelector(`[data-travel-total-vid='${CSS.escape(String(v.id))}']`)
    if (!wrap) return
    const t = this._computeAnnualTravel({ servicesLocation: v.servicesLocation, mileage: v.mileage, schedule: r.schedule })
    wrap.innerHTML = t
      ? this._formatTravel(t, { claim: !!v.includeMileageInPdf, rate: this.plan?.meta?.mileageRate })
      : `<span class="text-gray-400 italic">Enter Mileage + a Schedule row to compute</span>`
  }

  updateResourceField(event) {
    const el = event.currentTarget
    const rid = el.dataset.resourceId
    const field = el.dataset.field
    const r = this._resource(rid)
    if (!r || !field) return
    r[field] = el.value
    if (["vendorName","vendorPhone","vendorEmail","vendorWebsite","vendorDetails","vendorAddress"].includes(field)) {
      r.currentServices = this._composeCurrentServices(r)
    }
    if (field === "mileage") this._refreshTravelTotal(r)
    if (field === "cost") this._refreshCostTotal(r)
    // Mileage is computed on-demand — the user clicks "Calculate" on
    // the Mileage row to fire _autoComputeMileage. We do NOT auto-run
    // it on Service Address changes; that triggered Nominatim rate
    // limiting and the user wanted explicit control over when calls
    // happen.
    this._persistDebounced()
  }

  // Click handler for the Calculate button next to Mileage. Always
  // attempts a fresh routing call, even if Mileage is already filled.
  calculateMileage(event) {
    event.preventDefault()
    const rid = event.currentTarget.dataset.resourceId
    const r = this._resource(rid)
    if (!r) return
    // Force a recompute by clearing the existing value first so the
    // "user-typed value" guard doesn't bail early.
    r.mileage = ""
    this._autoComputeMileage(r)
  }

  // Toggle whether the travel block (mileage + total annual travel)
  // is rendered on the PDF. Leaf update — no re-render needed; the
  // print preview / PDF picks the value up on next render.
  toggleMileageInPdf(event) {
    const el = event.currentTarget
    const r = this._resource(el.dataset.resourceId)
    if (!r || !Array.isArray(r.vendors)) return
    const v = r.vendors.find(x => String(x.id) === String(el.dataset.vendorId))
    if (!v) return
    const claiming = el.checked
    // First time the user opts in to claim mileage we ask for the
    // per-mile reimbursement rate (IRS 2026 default: $0.70/mi).
    // Stored at the plan level so every resource uses the same rate.
    if (claiming) {
      this.plan.meta = this.plan.meta || {}
      let rate = parseFloat(this.plan.meta.mileageRate)
      if (!isFinite(rate) || rate <= 0) {
        const entered = window.prompt(
          "Mileage reimbursement rate ($ per mile):\n\nIRS 2026 standard is $0.70/mile. You can change this later on any goal.",
          "0.70"
        )
        const parsed = parseFloat(String(entered || "").replace(/[^0-9.]/g, ""))
        if (!isFinite(parsed) || parsed <= 0) {
          // User cancelled or entered something invalid — undo the
          // check so the box stays unchecked.
          el.checked = false
          return
        }
        rate = parsed
        this.plan.meta.mileageRate = rate
      }
    }
    v.includeMileageInPdf = claiming
    this._persist({ nested: true })
    this._refreshVendorTravel(r, v)
  }

  // Toggle whether this vendor's cost is included on the plan / PDF. OFF by
  // default (undefined ⇒ unchecked). The per-vendor cost preview always shows;
  // only the resource-level total + the PDF/plan count an included vendor.
  toggleIncludeCost(event) {
    const el = event.currentTarget
    const r = this._resource(el.dataset.resourceId)
    if (!r || !Array.isArray(r.vendors)) return
    const v = r.vendors.find(x => String(x.id) === String(el.dataset.vendorId))
    if (!v) return
    v.includeCost = el.checked
    this._persist({ nested: true })
    this._refreshCostTotal(r)   // update the resource-level total now that this vendor's inclusion changed
  }

  // Services Location dropdown — re-render so the conditional
  // "Center Location" field appears/disappears with the choice.
  // Plain updateResourceField only persists, so we hop through this
  // dedicated handler when location changes.
  updateResourceLocation(event) {
    const el = event.currentTarget
    const rid = el.dataset.resourceId
    const r = this._resource(rid)
    if (!r) return
    r.servicesLocation = el.value
    // Service Address + Mileage only make sense when the service
    // happens at a physical site that isn't the participant's home.
    // Clear them for "In Home" (no travel) and "Telehealth" (no
    // physical address) so stale data doesn't linger.
    if (el.value === "In Home" || el.value === "Telehealth") {
      r.vendorAddress = ""
      r.mileage = ""
    }
    r.currentServices = this._composeCurrentServices(r)
    // nested:true → dynamic-list won't re-render the whole goal
    // list and we keep the user's scroll position.
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  // Build the legacy r.currentServices display string from the
  // split fields so existing review/PDF readers (which still read
  // currentServices) render the combined "In Center — Vendor X ·
  // Site Y" text without needing to learn the new schema.
  _composeCurrentServices(r) {
    const vendors = Array.isArray(r.vendors) ? r.vendors : []
    const lines = vendors.map(v => {
      const parts = []
      if (v.servicesLocation) parts.push(v.servicesLocation)
      const bits = [v.name, v.phone, v.email].filter(Boolean)
      if (bits.length) parts.push(bits.join(" · "))
      if (v.address)   parts.push(v.address)
      return parts.join(" — ")
    }).filter(Boolean)
    return lines.join("; ")
  }

  // Auto-compute one-way DRIVING mileage from home → service address.
  //   1. Geocode both addresses via Nominatim (lat / lng)
  //   2. Ask OSRM (Open Source Routing Machine, public demo server) for
  //      the driving distance between them — meters → miles.
  //   3. Fall back to haversine straight-line distance only if OSRM
  //      can't be reached, with a "≈" prefix so the user knows.
  // Only fills r.mileage when it's empty — never overwrites a manual
  // value. Per-pair cache so we don't refetch on every re-render.
  async _autoComputeMileage(r) {
    if (!r) return
    if (r.servicesLocation === "In Home" || r.servicesLocation === "Telehealth") return
    const existing = String(r.mileage || "").trim()
    const isAutoFill = /^≈/.test(existing) || /\((est|osrm|ors)\)$/.test(existing)
    if (existing && !isAutoFill) return
    const home = this._composeHomeAddress()
    const dest = (r.vendorAddress || "").trim()
    if (!home || !dest) return

    // Geocode both addresses up-front — we need coords for the
    // server's ORS path AND for any client-side OSRM fallback.
    const [a, b] = await Promise.all([
      this._geocode(home),
      this._geocode(dest)
    ])
    if (!a || !b) return

    // 1) Server-side provider chain: Google (if GOOGLE_MAPS_API_KEY
    //    set) → ORS (if OPENROUTESERVICE_API_KEY set). Either one
    //    returning a valid number stops the chain.
    let miles = NaN
    let suffix = ""
    const server = await this._serverMiles(home, dest, a, b)
    if (server && isFinite(server.miles) && server.miles > 0) {
      miles = server.miles
      suffix = server.source === "google" ? "" : ` (${server.source})`
    } else {
      // 2) Public OSRM mirrors — accurate but ~6% short of Google.
      const osrm = await this._osrmMiles(a, b)
      if (isFinite(osrm) && osrm > 0) {
        miles = osrm
        suffix = " (osrm)"
      } else {
        // 3) Haversine × 1.4 urban driving correction — last resort.
        miles = this._haversine(a, b) * 1.4
        suffix = " (est)"
      }
    }
    if (!isFinite(miles) || miles <= 0) return
    const rounded = (miles < 10 ? miles.toFixed(1) : Math.round(miles).toString())

    r.mileage = `${rounded} mi${suffix}`
    // Persist + reflect in the on-screen input + recompute the total.
    // Also dispatch plan:change with nested:true so plan-history
    // captures the new state for undo/redo (without making dynamic-list
    // re-render the parent row).
    this._persist({ nested: true })
    const input = this.listTarget?.querySelector(
      `input[data-resource-id='${CSS.escape(String(r.id))}'][data-field='mileage']`
    )
    if (input) {
      const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set
      if (setter) setter.call(input, r.mileage); else input.value = r.mileage
    }
    this._refreshTravelTotal(r)
  }

  // Server-side provider chain: hits /api/distance which decides
  // (Google → ORS → no_provider) based on which API keys are set on
  // the deploy. Returns { miles, source } on success, null otherwise.
  async _serverMiles(origin, destination, originCoords, destCoords) {
    this.constructor._serverCache = this.constructor._serverCache || {}
    const cacheKey = `${origin}|${destination}`
    if (Object.prototype.hasOwnProperty.call(this.constructor._serverCache, cacheKey)) {
      return this.constructor._serverCache[cacheKey]
    }
    try {
      const csrf = document.querySelector("meta[name='csrf-token']")?.content || ""
      const res = await fetch("/api/distance", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrf
        },
        body: JSON.stringify({
          origin, destination,
          origin_lat:      originCoords?.lat, origin_lng:      originCoords?.lng,
          destination_lat: destCoords?.lat,   destination_lng: destCoords?.lng
        })
      })
      if (!res.ok) { this.constructor._serverCache[cacheKey] = null; return null }
      const json = await res.json()
      if (json?.ok && isFinite(parseFloat(json.miles))) {
        const out = { miles: parseFloat(json.miles), source: json.source || "server" }
        this.constructor._serverCache[cacheKey] = out
        return out
      }
      this.constructor._serverCache[cacheKey] = null
      return null
    } catch (_) {
      this.constructor._serverCache[cacheKey] = null
      return null
    }
  }

  // Try multiple OSRM mirrors in order so transient failures or
  // rate-limits on one don't kill the auto-mileage feature. Returns
  // driving distance in miles, or NaN if every mirror fails. Cached
  // per coord-pair so back-to-back resources sharing the same routing
  // don't refetch.
  async _osrmMiles(a, b) {
    this.constructor._osrmCache = this.constructor._osrmCache || {}
    const key = `${a.lat.toFixed(5)},${a.lng.toFixed(5)}|${b.lat.toFixed(5)},${b.lng.toFixed(5)}`
    if (Object.prototype.hasOwnProperty.call(this.constructor._osrmCache, key)) {
      const cached = this.constructor._osrmCache[key]
      if (isFinite(cached) && cached > 0) return cached
      // cached miss — fall through and try the network again
    }
    const mirrors = [
      `https://router.project-osrm.org/route/v1/driving/${a.lng},${a.lat};${b.lng},${b.lat}?overview=false`,
      `https://routing.openstreetmap.de/routed-car/route/v1/driving/${a.lng},${a.lat};${b.lng},${b.lat}?overview=false`
    ]
    for (const url of mirrors) {
      try {
        const res = await fetch(url)
        if (!res.ok) continue
        const json = await res.json()
        const meters = json?.routes?.[0]?.distance
        if (!isFinite(meters) || meters <= 0) continue
        const miles = meters * 0.000621371
        this.constructor._osrmCache[key] = miles
        return miles
      } catch (_) { /* try next mirror */ }
    }
    this.constructor._osrmCache[key] = NaN
    return NaN
  }

  _composeHomeAddress() {
    const c = this.plan?.client || {}
    const parts = [c.address, c.city, c.state, c.zip].map(s => (s || "").trim()).filter(Boolean)
    return parts.join(", ")
  }

  async _geocode(addr) {
    if (!addr) return null
    this.constructor._geoCache = this.constructor._geoCache || {}
    if (Object.prototype.hasOwnProperty.call(this.constructor._geoCache, addr)) {
      return this.constructor._geoCache[addr]
    }
    try {
      // Go through our server proxy /api/geocode (ORS Pelias →
      // Nominatim fallback, server-side cache). Same-origin, no CORS.
      const url = `/api/geocode?q=${encodeURIComponent(addr)}`
      const res = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!res.ok) { this.constructor._geoCache[addr] = null; return null }
      const json = await res.json()
      const hit = Array.isArray(json?.results) && json.results[0]
      if (!hit) { this.constructor._geoCache[addr] = null; return null }
      const coords = { lat: parseFloat(hit.lat), lng: parseFloat(hit.lon) }
      this.constructor._geoCache[addr] = coords
      return coords
    } catch (_) {
      this.constructor._geoCache[addr] = null
      return null
    }
  }

  _haversine(a, b) {
    const R = 3958.8 // earth radius in miles
    const toRad = (d) => d * Math.PI / 180
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const lat1 = toRad(a.lat), lat2 = toRad(b.lat)
    const s = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
    return 2 * R * Math.asin(Math.sqrt(s))
  }

  // Annual travel = sum(trips per schedule row) × one-way mileage × 2.
  // Returns { annualMiles, annualTrips, mileagePerTrip } or null when
  // we can't compute (no mileage, no schedule, in-home / telehealth).
  _computeAnnualTravel(r) {
    if (!r) return null
    if (r.servicesLocation === "In Home" || r.servicesLocation === "Telehealth") return null
    const m = parseFloat(String(r.mileage || "").replace(/[^0-9.]/g, ""))
    if (!isFinite(m) || m <= 0) return null
    const sched = Array.isArray(r.schedule) ? r.schedule : []
    let annualTrips = 0
    sched.forEach(s => {
      const times = parseInt(String(s.times || "1").replace(/[^0-9]/g, ""), 10) || 1
      const periods = ANNUAL_PERIODS[s.dayType] || 0
      annualTrips += times * periods
    })
    if (annualTrips <= 0) return null
    const annualMiles = annualTrips * m * 2  // round trip
    return { annualMiles, annualTrips, mileagePerTrip: m }
  }

  // Parse a cost string like "$95/hr", "$120/session", "$3200/mo" into
  // { amount: 95, unit: "hr" }. Returns null when no number is present.
  _parseCost(s) {
    const txt = String(s || "").trim()
    if (!txt) return null
    const m = txt.match(/-?\$?\s*([\d,]+(?:\.\d+)?)\s*\/?\s*([a-z]+)?/i)
    if (!m) return null
    const amount = parseFloat(m[1].replace(/,/g, ""))
    if (!isFinite(amount)) return null
    const unitRaw = (m[2] || "").toLowerCase()
    let unit = "session"   // sensible default if no /unit token
    if (/^hr|hour/.test(unitRaw))       unit = "hour"
    else if (/^min/.test(unitRaw))      unit = "minute"
    else if (/^session|sess/.test(unitRaw)) unit = "session"
    else if (/^wk|week/.test(unitRaw))  unit = "week"
    else if (/^mo|month/.test(unitRaw)) unit = "month"
    else if (/^yr|year|ann/.test(unitRaw)) unit = "year"
    else if (/^visit/.test(unitRaw))    unit = "session"
    return { amount, unit }
  }

  // Compute per-session, per-period (week / month etc.), and annual
  // costs given the resource's cost string + schedule rows. Returns
  // null when we can't compute meaningfully.
  _computeAnnualCost(r) {
    if (!r) return null
    const cost = this._parseCost(r.cost)
    if (!cost) return null
    const sched = Array.isArray(r.schedule) ? r.schedule : []
    if (!sched.length) {
      // No schedule yet — for month / week / year fixed costs we can
      // still extrapolate; for hour / session we can't (need a schedule).
      if (cost.unit === "month") return { annualCost: cost.amount * 12,  periodCost: cost.amount, periodLabel: "month" }
      if (cost.unit === "week")  return { annualCost: cost.amount * 52,  periodCost: cost.amount, periodLabel: "week" }
      if (cost.unit === "year")  return { annualCost: cost.amount,        periodCost: cost.amount, periodLabel: "year" }
      return null
    }
    let annualCost = 0
    let dominantDayType = sched[0]?.dayType || "Weekly"
    sched.forEach(s => {
      const times   = parseInt(String(s.times || "1").replace(/[^0-9]/g, ""), 10) || 1
      const periods = ANNUAL_PERIODS[s.dayType] || 0
      const hours   = parseFloat(s.hours || "0") || 0
      const mins    = parseFloat(s.mins  || "0") || 0
      const durationHrs = hours + (mins / 60)
      const sessionsPerYear = times * periods
      let perSession = 0
      switch (cost.unit) {
        case "hour":    perSession = cost.amount * durationHrs; break
        case "minute":  perSession = cost.amount * (hours * 60 + mins); break
        case "session": perSession = cost.amount; break
        case "week":    perSession = cost.amount / Math.max(times, 1); break  // amount IS the per-week rate
        case "month":   annualCost += cost.amount * 12; return                 // fixed monthly, ignore schedule once
        case "year":    annualCost += cost.amount; return
        default:        perSession = cost.amount
      }
      annualCost += perSession * sessionsPerYear
    })
    // Per-period cost (week / month / year) based on the dominant
    // day type — most schedules are weekly so this reads naturally.
    const periodMap = { Weekly: 52, "Bi-Weekly": 26, Monthly: 12, Quarterly: 4, "Semi-Annually": 2, Annually: 1, Daily: 365, "Mon-Fri": 260, "Sat-Sun": 104 }
    const periodsPerYear = periodMap[dominantDayType] || 52
    const periodLabel = dominantDayType === "Monthly" ? "month"
                      : dominantDayType === "Annually" ? "year"
                      : dominantDayType === "Daily" ? "day"
                      : "week"
    const periodCost = annualCost / periodsPerYear
    return { annualCost, periodCost, periodLabel }
  }

  _formatCostRollup(c) {
    if (!c) return ""
    const fmt = (n) => "$" + Math.round(n).toLocaleString()
    return `${fmt(c.periodCost)}/${c.periodLabel} · <strong>${fmt(c.annualCost)}/year</strong>`
  }

  _refreshCostTotal(r) {
    if (!r) return
    const wrap = this.listTarget?.querySelector(`[data-cost-total-rid='${CSS.escape(String(r.id))}']`)
    if (!wrap) return
    const c = this._computeVendorsCost(r)
    wrap.innerHTML = c
      ? this._formatCostRollup(c)
      : `<span class="text-gray-400 italic">Enter a vendor Cost + Frequency to compute</span>`
  }

  _formatTravel(t, opts = {}) {
    if (!t) return ""
    const fmt = (n) => n >= 100
      ? Math.round(n).toLocaleString()
      : n.toLocaleString(undefined, { maximumFractionDigits: 1 })
    const usd = (n) => "$" + Math.round(n).toLocaleString()
    // Show the round-trip multiplier explicitly so the math reads.
    // `annualTrips` = sessions per year; each session is a round trip
    // (×2 the one-way mileage).
    let out = `${fmt(t.annualMiles)} mi/year ` +
              `<span class="font-normal text-gray-500">` +
              `(${t.annualTrips.toLocaleString()} sessions × ${fmt(t.mileagePerTrip)} mi × 2)` +
              `</span>`
    // When the user has opted in to claim mileage, append the dollar
    // figure computed at the plan-level reimbursement rate.
    const rate = parseFloat(opts.rate)
    if (opts.claim && isFinite(rate) && rate > 0) {
      const dollars = t.annualMiles * rate
      out += ` · <strong>${usd(dollars)}/year</strong> ` +
             `<span class="font-normal text-gray-500">@ $${rate.toFixed(2)}/mi</span>`
    }
    return out
  }

  // Live update — called from updateResourceField / updateScheduleField
  // when mileage or a schedule cell changes. Updates the inline value
  // span without a re-render so the focused input keeps its caret.
  _refreshTravelTotal(r) {
    if (!r) return
    const wrap = this.listTarget?.querySelector(`[data-travel-total-rid='${CSS.escape(String(r.id))}']`)
    if (!wrap) return
    const t = this._computeAnnualTravel(r)
    if (!t) {
      wrap.innerHTML = `<span class="text-gray-400 italic">Enter Mileage + a Schedule row to compute</span>`
      return
    }
    wrap.innerHTML = this._formatTravel(t, {
      claim: !!r.includeMileageInPdf,
      rate:  this.plan?.meta?.mileageRate
    })
  }

  // Build a Google Maps directions URL from client home → vendor.
  // We open it in a new tab so the user can read the round-trip
  // mileage off Maps' summary card and copy it back into the
  // Mileage field. No API key needed.
  openMileage(event) {
    event.preventDefault()
    const el = event.currentTarget
    const r = this._resource(el.dataset.resourceId)
    if (!r || !Array.isArray(r.vendors)) return
    const v = r.vendors.find(x => String(x.id) === String(el.dataset.vendorId))
    if (!v || !v.address) {
      alert("Enter a Vendor Address first.")
      return
    }
    const c = this.plan.client || {}
    const origin = [c.address, c.city, c.state, c.zip].filter(Boolean).join(", ")
    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(v.address)}`
    window.open(url, "_blank", "noopener")
  }

  // Tag pill click — segmented control behavior.
  setResourceTag(event) {
    const btn = event.currentTarget
    const rid = btn.dataset.resourceId
    const tag = btn.dataset.tag
    const r = this._resource(rid)
    if (!r || !tag) return
    r.tag = tag
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  addSchedule(event) {
    const rid = event.currentTarget.dataset.resourceId
    const r = this._resource(rid)
    if (!r) return
    if (!Array.isArray(r.schedule)) r.schedule = []
    const newSid = Date.now() + Math.random()
    r.schedule.push({
      id: newSid,
      dayType: "Weekly", times: "", unit: "times", hours: "", mins: "", cost: ""
    })
    this._persist({ nested: true })
    this._renderPreserveScroll()
    // Move focus onto the new row's first input so the browser
    // keeps the row visible AND the user can immediately start
    // typing — both prevent the perceived page-jump.
    requestAnimationFrame(() => {
      const first = this.listTarget.querySelector(`[data-schedule-id="${newSid}"][data-field="times"]`)
      if (first) first.focus({ preventScroll: true })
    })
  }

  removeSchedule(event) {
    const btn = event.currentTarget
    const rid = btn.dataset.resourceId
    const sid = btn.dataset.scheduleId
    const r = this._resource(rid)
    if (!r || !Array.isArray(r.schedule)) return
    r.schedule = r.schedule.filter(s => String(s.id) !== String(sid))
    this._persist({ nested: true })
    this._renderPreserveScroll()
  }

  updateScheduleField(event) {
    const el = event.currentTarget
    const rid = el.dataset.resourceId
    const sid = el.dataset.scheduleId
    const field = el.dataset.field
    const r = this._resource(rid)
    if (!r || !Array.isArray(r.schedule)) return
    const s = r.schedule.find(x => String(x.id) === String(sid))
    if (!s) return
    // TBD checkboxes (Assessment needed / Weekly Schedule needed) swap the
    // hrs/min inputs for the tag — persist immediately + re-render.
    if (el.type === "checkbox") {
      s[field] = el.checked
      if (el.checked) { s.hours = ""; s.mins = "" }
      ;(r.vendors || []).forEach(v => { this._refreshVendorCost(r, v); this._refreshVendorTravel(r, v) })
      this._persist({ nested: true })
      this._renderPreserveScroll()
      return
    }
    s[field] = el.value
    // Frequency is shared across vendors — refresh each vendor's own
    // Total Cost / Travel (there is no combined total).
    ;(r.vendors || []).forEach(v => { this._refreshVendorCost(r, v); this._refreshVendorTravel(r, v) })
    this._persistDebounced()
  }

  // ---- render ----
  render() {
    if (!this.hasListTarget) return
    const g = this._goal()
    if (!g) { this.listTarget.innerHTML = ""; return }
    const resources = g.resources || []
    this.listTarget.innerHTML = resources.length
      ? resources.map(r => this._renderResource(r)).join("")
      : `<p class="text-xs italic text-gray-400 py-2">No resources yet. Click + Add Resource to start.</p>`
    // Re-hydrate the Lucide placeholders we just injected (vendor trash,
    // resource trash, the map-pin "Lookup" button). Without this every
    // re-render leaves `<i data-lucide>` empty, so the delete/remove
    // buttons render invisible. editor_native only hydrates on section
    // switch, not on our in-place list rebuilds.
    renderIcons(this.element)
    // Auto-compute mileage is triggered EXPLICITLY when the Service
    // Address field changes (updateResourceField). We don't fire it
    // on every render — that would re-geocode home/dest on every
    // plan:change (e.g., while the user types in the Client section),
    // hammering Nominatim into a 429 rate-limit and breaking the
    // autocomplete for the rest of the session.
  }

  // Renders without yanking the scroll position. Each mutation
  // (add resource, add schedule, change tag, …) replaces listTarget's
  // innerHTML, which detaches the currently-focused element and the
  // browser's scroll anchor — the page then jumps to the top of the
  // editor pane. We capture the nearest scrolling ancestor's scrollTop
  // before the swap and restore it right after.
  _renderPreserveScroll() {
    const scrollParent = this._findScrollParent(this.element)
    const top  = scrollParent ? scrollParent.scrollTop  : 0
    const left = scrollParent ? scrollParent.scrollLeft : 0
    this.render()
    if (scrollParent) {
      scrollParent.scrollTop  = top
      scrollParent.scrollLeft = left
    }
  }

  _findScrollParent(el) {
    let p = el?.parentElement
    while (p && p !== document.body) {
      const cs = getComputedStyle(p)
      const oy = cs.overflowY
      if ((oy === "auto" || oy === "scroll" || oy === "overlay") && p.scrollHeight > p.clientHeight) {
        return p
      }
      p = p.parentElement
    }
    return document.scrollingElement || document.documentElement
  }

  // Ensure r.vendors exists, migrating the legacy flat vendor / location
  // / cost fields into a single vendor the first time we encounter an
  // older resource so its data opens intact. Runs once (guarded).
  _ensureVendors(r) {
    if (Array.isArray(r.vendors)) return r.vendors
    const hasFlat = r.vendorName || r.vendorPhone || r.vendorEmail ||
                    r.vendorWebsite || r.vendorAddress || r.vendorDetails ||
                    r.servicesLocation || r.cost
    r.vendors = hasFlat ? [{
      id: Date.now() + Math.random(),
      name: r.vendorName || "", phone: r.vendorPhone || "",
      email: r.vendorEmail || "", website: r.vendorWebsite || "",
      address: r.vendorAddress || "", servicesLocation: r.servicesLocation || "",
      cost: r.cost || ""
    }] : []
    return r.vendors
  }

  // One vendor block — contact fields + this vendor's own Services
  // Location + Cost + a remove button. Fields write via updateVendorField
  // keyed by resource-id + vendor-id.
  //
  // Three modes based on vendorId linkage:
  // 1. Linked (vendorId set, library record exists): read-only display with
  //    "edit in library" link, only cost is editable (plan-specific).
  // 2. Orphaned (vendorId set, library record missing): editable fields with
  //    warning about missing library record.
  // 3. Unlinked (no vendorId): fully editable fields with vendor-picker to link.
  _renderVendor(r, v, idx) {
    const rid = r.id
    const vid = v.id
    const F = (field) => `data-action="input->goal-resources#updateVendorField" data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}" data-field="${field}"`
    const travel = this._isTravelLoc(v.servicesLocation)

    // Resolve vendor display data (library vs legacy fields)
    const display = resolveVendorDisplay(v, vendorGet)
    const isLinked = display.linked
    const isOrphan = display.unlinked

    // "Choose from saved vendors" — offered when the owning goal came from a
    // library family that has a curated vendor list. Picking one fills the
    // name / contact fields on this vendor row. Only shown when not linked.
    const _g = this._goal()
    const savedVendors = (_g && typeof _g.libXxx === "number" && !isLinked)
      ? (libFamily(_g.libXxx).vendors || []).filter(x => x && x.name) : []
    const savedVendorPicker = savedVendors.length ? `
        <div class="mb-2">
          <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Choose from saved vendors</label>
          <select data-action="change->goal-resources#fillVendorFromLibrary"
                  data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}"
                  class="w-full p-1.5 border border-indigo-300 rounded text-xs bg-indigo-50 text-indigo-900">
            <option value="">— Choose from saved vendors —</option>
            ${savedVendors.map(sv => `<option value="${esc(sv.name)}" data-phone="${esc(sv.phone || "")}" data-email="${esc(sv.email || "")}" data-website="${esc(sv.website || "")}" data-address="${esc(sv.address || "")}">${esc(sv.name)}</option>`).join("")}
          </select>
        </div>` : ""

    const costC  = this._computeAnnualCost({ cost: v.cost, schedule: r.schedule })
    const travC  = this._computeAnnualTravel({ servicesLocation: v.servicesLocation, mileage: v.mileage, schedule: r.schedule })

    // Header: vendor number + remove button + optional "edit in library" link
    const headerActions = isLinked
      ? `<a href="/editor/native/settings#vendors" class="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-500 hover:text-indigo-700 mr-2"
            title="Edit vendor details in Settings → Vendors">
           <i data-lucide="external-link" class="w-3 h-3"></i> Edit in library
         </a>`
      : ""
    const unlinkBtn = isLinked
      ? `<button type="button"
                 data-action="goal-resources#unlinkVendor"
                 data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}"
                 class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-400 hover:bg-gray-100 hover:text-gray-600 mr-1"
                 title="Unlink from vendor library">
           <i data-lucide="unlink" class="w-3 h-3"></i> Unlink
         </button>`
      : ""

    // Orphan warning
    const orphanWarning = isOrphan ? `
      <div class="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
        <i data-lucide="alert-triangle" class="w-3 h-3 inline-block mr-1"></i>
        Vendor not found in library. Fields are editable below.
      </div>` : ""

    // Contact fields: read-only when linked, editable otherwise
    const contactFields = isLinked ? `
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Vendor Name</label>
            <div class="w-full p-1.5 border border-gray-200 rounded text-xs bg-gray-100 text-gray-700">${esc(display.name || "(unnamed)")}</div>
          </div>
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Phone</label>
            <div class="w-full p-1.5 border border-gray-200 rounded text-xs bg-gray-100 text-gray-700">${display.phone ? esc(display.phone) : '<span class="text-gray-400">—</span>'}</div>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Email</label>
            <div class="w-full p-1.5 border border-gray-200 rounded text-xs bg-gray-100 text-gray-700">${display.email ? esc(display.email) : '<span class="text-gray-400">—</span>'}</div>
          </div>
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Website</label>
            <div class="w-full p-1.5 border border-gray-200 rounded text-xs bg-gray-100 text-gray-700">${display.website ? esc(display.website) : '<span class="text-gray-400">—</span>'}</div>
          </div>
        </div>
        <div class="mb-2">
          <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Vendor Address</label>
          <div class="w-full p-1.5 border border-gray-200 rounded text-xs bg-gray-100 text-gray-700">${display.address ? esc(display.address) : '<span class="text-gray-400">—</span>'}</div>
        </div>` : `
        ${savedVendorPicker}
        <div class="mb-2">
          <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Link to Vendor Library</label>
          <div class="gr-vendor-picker"
               data-controller="vendor-picker"
               data-vendor-picker-value-value=""
               data-vendor-picker-name-value=""
               data-vendor-picker-placeholder-value="Search or add vendor..."
               data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}"
               data-action="vendor-picker:change->goal-resources#handleVendorPickerChange">
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Vendor Name</label>
            <input type="text" placeholder="Vendor / agency name" value="${esc(v.name || "")}" ${F("name")}
                   class="w-full p-1.5 border border-gray-300 rounded text-xs">
          </div>
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Phone</label>
            <input type="tel" placeholder="(555) 555-1234" value="${esc(v.phone || "")}" ${F("phone")}
                   class="w-full p-1.5 border border-gray-300 rounded text-xs">
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Email</label>
            <input type="email" placeholder="vendor@example.com" value="${esc(v.email || "")}" ${F("email")}
                   class="w-full p-1.5 border border-gray-300 rounded text-xs">
          </div>
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Website</label>
            <input type="url" placeholder="https://…" value="${esc(v.website || "")}" ${F("website")}
                   class="w-full p-1.5 border border-gray-300 rounded text-xs">
          </div>
        </div>
        <div class="mb-2">
          <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Vendor Address</label>
          <input type="text" placeholder="Street, city, state, ZIP" value="${esc(v.address || "")}"
                 data-controller="address-autocomplete" ${F("address")}
                 class="w-full p-1.5 border border-gray-300 rounded text-xs">
        </div>`

    return `
      <div class="border border-gray-200 rounded p-2 mb-2 ${isLinked ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50'}">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold uppercase text-gray-500">
            Vendor ${idx + 1}
            ${isLinked ? '<span class="ml-1 px-1 py-0.5 bg-indigo-100 text-indigo-600 rounded text-[9px]">LINKED</span>' : ''}
          </span>
          <div class="flex items-center">
            ${headerActions}
            ${unlinkBtn}
            <button type="button"
                    data-action="goal-resources#removeVendor"
                    data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}"
                    class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-500 hover:bg-red-50 hover:text-red-600"
                    title="Remove this vendor">
              <i data-lucide="trash-2" class="w-3.5 h-3.5"></i> Remove
            </button>
          </div>
        </div>
        ${orphanWarning}
        ${contactFields}
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Services Location</label>
            <select data-action="change->goal-resources#updateVendorField"
                    data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}" data-field="servicesLocation"
                    class="w-full p-1.5 border border-gray-300 rounded text-xs bg-white">
              ${["", "In Home", "In Center", "Community-Based", "Telehealth", "Hybrid", "Other"]
                .map(opt => `<option value="${esc(opt)}" ${v.servicesLocation === opt ? "selected" : ""}>${opt ? esc(opt) : "— Select —"}</option>`)
                .join("")}
            </select>
          </div>
          <div>
            <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Cost <span class="font-normal text-gray-400">(plan-specific)</span></label>
            <input type="text" placeholder="e.g. $95/hr" value="${esc(v.cost || "")}" ${F("cost")}
                   class="w-full p-1.5 border border-gray-300 rounded text-xs">
          </div>
        </div>
        <div class="flex items-center gap-2 mb-2" data-vendor-mileage-row data-vendor-id="${esc(vid)}"${travel ? "" : " hidden"}>
          <label class="text-[10px] font-bold uppercase text-gray-500">Mileage</label>
          <input type="text" placeholder="e.g. 12 mi" value="${esc(v.mileage || "")}" ${F("mileage")}
                 class="w-24 p-1.5 border border-gray-300 rounded text-xs">
          <button type="button" data-action="goal-resources#openMileage"
                  data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}"
                  class="p-1.5 text-primary hover:bg-primary/10 rounded text-xs font-bold flex items-center gap-1"
                  title="Open Google Maps to see the route and copy the mileage here">
            <i data-lucide="map-pin" class="w-3 h-3"></i> Lookup
          </button>
        </div>
        <div class="mt-1 pt-2 border-t border-gray-200 flex items-baseline gap-2 flex-wrap">
          <span class="text-[10px] font-bold uppercase text-gray-500">Total Cost</span>
          <span class="text-xs font-semibold text-gray-700" data-cost-total-vid="${esc(vid)}">
            ${costC ? this._formatCostRollup(costC) : `<span class="text-gray-400 italic">Enter Cost + Frequency to compute</span>`}
          </span>
          <label class="ml-auto flex items-center gap-1 text-[10px] font-bold uppercase text-gray-500 cursor-pointer select-none">
            <input type="checkbox" data-action="change->goal-resources#toggleIncludeCost"
                   data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}"
                   ${v.includeCost ? "checked" : ""} class="cursor-pointer">
            Include Cost
          </label>
        </div>
        <div class="mt-1 pt-2 border-t border-gray-200 flex items-baseline gap-2 flex-wrap" data-vendor-travel-row data-vendor-id="${esc(vid)}"${travel ? "" : " hidden"}>
          <span class="text-[10px] font-bold uppercase text-gray-500">Total Annual Travel</span>
          <span class="text-xs font-semibold text-gray-700" data-travel-total-vid="${esc(vid)}">
            ${travC ? this._formatTravel(travC, { claim: !!v.includeMileageInPdf, rate: this.plan?.meta?.mileageRate }) : `<span class="text-gray-400 italic">Enter Mileage + a Schedule row to compute</span>`}
          </span>
          <label class="ml-auto flex items-center gap-1 text-[10px] font-bold uppercase text-gray-500 cursor-pointer select-none">
            <input type="checkbox" data-action="change->goal-resources#toggleMileageInPdf"
                   data-resource-id="${esc(rid)}" data-vendor-id="${esc(vid)}"
                   ${v.includeMileageInPdf ? "checked" : ""} class="cursor-pointer">
            Claim Mileage
          </label>
        </div>
      </div>`
  }

  // Aggregate cost across all vendors, annualized by the shared frequency
  // (r.schedule). Every vendor uses the same schedule, so the per-period
  // label is consistent and we sum both the annual + period figures.
  _computeVendorsCost(r) {
    const vendors = Array.isArray(r.vendors) ? r.vendors : []
    let annualCost = 0, periodCost = 0, periodLabel = null
    vendors.forEach(v => {
      if (!v.includeCost) return   // only vendors with "Include Cost" checked count toward the total
      const c = this._computeAnnualCost({ cost: v.cost, schedule: r.schedule })
      if (c) { annualCost += c.annualCost; periodCost += c.periodCost; periodLabel = c.periodLabel }
    })
    return periodLabel ? { annualCost, periodCost, periodLabel } : null
  }

  _renderResource(r) {
    const rid = r.id
    const tagBtn = (t) => `
      <button type="button"
              data-action="goal-resources#setResourceTag"
              data-resource-id="${esc(rid)}"
              data-tag="${t.key}"
              class="gr-tag${r.tag === t.key ? " is-on" : ""}">
        ${t.label}
      </button>`
    const tags = TAGS.map(tagBtn).join(" ")
    const schedRows = scheduleRowsHtml(r.schedule, {
      controller: "goal-resources",
      esc,
      rowAttrs: (s) => `data-resource-id="${esc(rid)}" data-schedule-id="${esc(s.id)}"`,
      cls: {
        row:     "flex items-center gap-1 mb-1",
        daytype: "flex-1 p-1 border border-green-200 rounded text-xs bg-white",
        num:     "w-10 p-1 border border-green-200 rounded text-xs",
        unit:    "p-1 border border-green-200 rounded text-[10px] text-gray-600 bg-white",
        hrs:     "w-12 p-1 border border-green-200 rounded text-xs",
        min:     "w-12 p-1 border border-green-200 rounded text-xs",
        lbl:     "text-[10px] text-gray-500",
        del:     "gr-sched-del"
      }
    })

    const isUnmet = r.tag === "unmet"
    const subTitle = isUnmet ? "Currently Requesting" : "Currently Approved"
    const subBg = isUnmet ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"
    const subText = isUnmet ? "text-amber-700" : "text-green-700"
    const vendors = this._ensureVendors(r)
    return `
      <div class="border border-gray-200 rounded-lg p-3 mb-3 bg-white">
        <div class="flex items-center gap-2 flex-wrap mb-2">
          ${tags}
          <button type="button"
                  data-action="goal-resources#removeResource"
                  data-resource-id="${esc(rid)}"
                  class="ml-auto p-1 dl-row-remove"
                  title="Remove resource">
            <i data-lucide="trash-2" class="w-4 h-4"></i>
          </button>
        </div>
        <select data-action="change->goal-resources#changeService"
                data-resource-id="${esc(rid)}"
                class="w-full p-2 border border-gray-300 rounded text-sm mb-2 font-semibold bg-white">
          ${serviceOptions(r.text || r.type)}
        </select>
        <!-- "Specify the service" — always in the DOM but hidden unless
             the service is "Other". changeService toggles it in place so
             no re-render is needed (which would move the scroll). -->
        <div class="flex items-center gap-2 mb-2 flex-wrap" data-svc-other-row${(r.text || r.type) === "Other" ? "" : " hidden"}>
          <input type="text" placeholder="Specify the service…" value="${esc(r.comment || "")}"
                 data-action="input->goal-resources#updateResourceField"
                 data-resource-id="${esc(rid)}" data-field="comment"
                 class="flex-1 min-w-[10rem] p-2 border border-gray-300 rounded text-sm">
          <input type="text" placeholder="Category / sub-section" value="Other"
                 list="svc-cats-${esc(rid)}" data-svc-cat data-resource-id="${esc(rid)}"
                 title="Which sub-section to file it under — pick one or type a new one"
                 class="w-40 shrink-0 p-2 border border-gray-300 rounded text-sm">
          <datalist id="svc-cats-${esc(rid)}">${serviceLibCategories().map(c => `<option value="${esc(c)}"></option>`).join("")}</datalist>
          <button type="button" data-action="goal-resources#addServiceToLibrary"
                  data-resource-id="${esc(rid)}" data-svc-add
                  class="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
                  title="Save this service to your library under the chosen sub-section">➕ Add to my services</button>
        </div>
        <!-- Frequency — its own section, above the vendor block. The
             schedule rows still drive the Total Cost / Total Annual
             Travel rollups rendered further down in the panel. -->
        <div class="flex items-center gap-2 mb-2">
          <label class="text-[10px] font-bold uppercase text-gray-500">Ratio</label>
          <input type="text" placeholder="e.g. 2:1" value="${esc(r.ratio || "")}"
                 data-action="input->goal-resources#updateResourceField"
                 data-resource-id="${esc(rid)}" data-field="ratio"
                 class="w-20 p-1 border border-gray-300 rounded text-xs">
        </div>
        <div class="mb-3">
          <label class="text-[10px] font-bold uppercase text-gray-500 block text-left mb-1">Frequency</label>
          ${schedRows || `<p class="text-[10px] italic text-gray-400 py-1">No schedule rows yet.</p>`}
          <button type="button"
                  data-action="goal-resources#addSchedule"
                  data-resource-id="${esc(rid)}"
                  class="mt-1 text-[10px] font-bold text-primary hover:underline">
            + Add Frequency and Schedule
          </button>
        </div>
        <!-- Vendors — one block per provider. "+ Add Vendor" appends
             another; each carries its own contact + Services Location +
             Cost. Frequency (above) is shared; the Total Cost below sums
             every vendor across that frequency. -->
        ${vendors.map((v, i) => this._renderVendor(r, v, i)).join("")}
        <button type="button"
                data-action="goal-resources#addVendor"
                data-resource-id="${esc(rid)}"
                class="mt-1 mb-2 text-[10px] font-bold text-primary hover:underline">
          + Add Vendor
        </button>
      </div>`
  }

  // ---- state helpers ----
  _goal() {
    // Prefer the live row's data-id (set by dynamic-list after the
    // template clone), fall back to the static value attribute.
    const id = this._knownGoalId || this.goalIdValue
    if (!id) return null
    if (!Array.isArray(this.plan?.goals)) return null
    return this.plan.goals.find(g => String(g.id) === String(id))
  }
  _resource(rid) {
    const g = this._goal()
    if (!g || !Array.isArray(g.resources)) return null
    return g.resources.find(r => String(r.id) === String(rid))
  }
  // `nested:true` tells listeners (notably dynamic-list) that this is
  // a leaf field update inside a sub-list and they should NOT re-render
  // — otherwise the parent dynamic-list yanks the focused input on every
  // keystroke and the cursor jumps out.
  _persist({ nested = false } = {}) {
    savePlan(this.plan)
    this._suppress = true
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "goals", plan: this.plan, source: "goal-resources", nested }
    }))
    this._suppress = false
  }
  // Debounced persist for per-keystroke text fields — the field's model is
  // already updated synchronously by the caller, so only the save + nested
  // broadcast is coalesced. Flushed on disconnect.
  _persistDebounced() {
    ;(this._debPersist ||= debounce(() => this._persist({ nested: true }), 200, { dirty: true }))()
  }
  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
}
